<script>
    import { apiUrl } from "@/utils";
    import { sendAnalytics } from "@/core/SendAnalytics";
    import ProductCardButtons from "@/components/ProductCardButtons";
    import ProductColorsList from "@/components/ProductColorsList";
    import ProductPrice from "@khalatentse/components/ProductPrice";

    export default {
        name: "ProductCard",
        components: {
            ProductPrice,
            ProductCardButtons,
            ProductColorsList,
        },
        props: {
            product: Object,
            position: Number,
            isSmall: Boolean,
            inCartList: Array,
        },
        data() {
            let isPreOrder = false;
            let currentOffers = this.product.offers.filter((offer) => offer.available);

            if (currentOffers.length === 0) {
                // проверка на предзаказ
                currentOffers = this.product.offers.filter((offer) => !offer.available);
                isPreOrder = true;
            }

            const previewImage =
                currentOffers[0].previewImage?.medium || this.product.previewImage?.medium;

            return {
                currentOffers,
                offer: currentOffers[0],
                offerId: currentOffers[0].id,
                previewImage,
                price: currentOffers[0].price,
                discountPrice: currentOffers[0].discountPrice,
                discountPercent: currentOffers[0].discountPercent,
                inCart: this.product.inCart || false,
                isPreOrder,
                previewImageFromOffer: "",
            };
        },
        watch: {
            inCartList() {
                this.checkProductInCart();
            },
            offerId() {
                this.checkProductInCart();
            },
        },
        computed: {
            nameProduct() {
                return this.product.shortName ? this.product.shortName : this.product.name;
            },
            scrImage() {
                return this.previewImageFromOffer ? apiUrl(this.previewImageFromOffer) : "";
            },
            sizeButton() {
                return this.isSmall ? "sm" : "md";
            },
            productPath() {
                return {
                    name: "Product",
                    params: { slug: this.product.slug },
                };
            },
        },
        mounted() {
            this.checkProductInCart();
        },
        methods: {
            apiUrl,
            selectColor(color) {
                const offer = this.currentOffers.find((offer) => offer.color.id === color.id);
                if (offer) {
                    const { id, previewImage, price, discountPrice, available } = offer;
                    this.offerId = id;
                    this.previewImageFromOffer = previewImage.medium;
                    this.price = price;
                    this.discountPrice = discountPrice;
                    this.isPreOrder = !available;
                    this.inCart = false;
                }
            },
            checkProductInCart() {
                this.inCartList
                    ? (this.inCart = this.inCartList.includes(this.offerId.toString()))
                    : (this.inCart = false);
            },
            sendDataLayerEvent() {
                sendAnalytics.send(sendAnalytics.events.productClick, {
                    name: this.product.name,
                    offerId: this.offerId,
                    price: this.discountPrice,
                    position: this.position,
                });
            },
        },
    };
</script>

<template>
    <div
        class="product-card"
        :class="{ 'product-card--sm': isSmall, 'product-card--in-cart': inCart }"
    >
        <div class="product-card__header">
            <div class="product-card__img">
                <router-link
                    :to="productPath"
                    class="product-card__img-link js-lazy-load"
                    tabindex="-1"
                    @click.native.prevent="sendDataLayerEvent"
                >
                    <img
                        class="product-card__img-product"
                        :data-src="apiUrl(previewImage)"
                        :src="scrImage"
                    />

                    <div class="product-card__preloader">
                        <preloader :show="true"></preloader>
                    </div>
                </router-link>
            </div>
        </div>

        <div class="product-card__footer">
            <div v-if="offer.color && offer.available" class="product-card__colors">
                <product-colors-list
                    :offers="currentOffers"
                    :current-offer="offer"
                    @select-color="selectColor"
                ></product-colors-list>
            </div>
            <div v-else class="product-card__colors-spy"></div>
            <router-link
                :to="productPath"
                class="product-card__wrap"
                @click.native.prevent="sendDataLayerEvent"
            >
                <div class="product-card__name">
                    {{ nameProduct }}
                </div>

                <div class="product-card__price">
                    <product-price
                        :price="discountPrice"
                        :old-price="price"
                        :discount-count="discountPercent"
                        is-card-size
                        :is-small-size="isSmall"
                    ></product-price>
                </div>
            </router-link>

            <div class="product-card__buttons">
                <product-card-buttons
                    :offer-id="offerId"
                    :product="product"
                    :is-pre-order="isPreOrder"
                    :in-cart="inCart"
                    :discount-price="discountPrice"
                    :size-button="sizeButton"
                    @on-added-to-cart="inCart = true"
                ></product-card-buttons>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .product-card {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
        background-color: #fff;
        border-radius: 12px;
        border: 2px solid var(--primary);
        transition: border-color 0.2s ease-in;

        &.swiper-slide {
            height: auto;
        }

        &:hover {
            border-color: var(--primary--dark);
        }

        @include bp($bp-desktop-sm) {
            border-radius: var(--border-xl);
        }
    }

    .product-card__header {
        position: relative;
        padding: 6px 6px 8px;
        @include bp($bp-desktop-sm) {
            padding: 12px 12px 10px;
        }
    }

    .product-card__colors {
        margin-bottom: 10px;
    }

    .product-card__colors-spy {
        margin-bottom: 10px;
        height: 24px;
    }

    .product-card__img {
        position: relative;
        overflow: hidden;
    }

    .product-card__img-link {
        position: relative;
        display: block;
        padding-bottom: 100%;
        overflow: hidden;
        border-radius: 8px;

        img {
            @include center-absolute(0);
            margin: auto;
            height: 100%;
            max-height: 100%;
            border-radius: 8px;
            object-fit: cover;
        }

        @include bp($bp-desktop-sm) {
            border-radius: var(--border-md);
            img {
                border-radius: var(--border-md);
            }
        }
    }

    .product-card__preloader {
        opacity: 1;
    }

    .product-card__img-product {
        z-index: 1;
        display: none;
        transition: opacity 0.3s ease-in;

        animation: fadeIn 0.3s ease-in-out;

        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: scale(0.9);
            }
            to {
                opacity: 1;
                transform: scale(0.98);
            }
        }

        &[lazy="loaded"] {
            display: block;

            & ~ .product-card__preloader {
                opacity: 0;
            }
        }
    }

    .product-card__footer {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        padding: 0 8px 8px;
        @include bp($bp-desktop-sm) {
            padding: 0 16px 20px;
        }
    }

    .product-card__buttons {
        width: 100%;
    }

    .product-card__wrap {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        padding: 0 4px;
        @include bp($bp-desktop-sm) {
            padding: 0 8px;
        }
    }

    .product-card__name {
        position: relative;
        z-index: 2;
        display: block;
        flex-grow: 1;
        margin-bottom: 8px;
        color: var(--primary-text);
        font-size: 14px;
        line-height: 18px;
        font-weight: 500;
        letter-spacing: -0.02em;
        transition: 0.3s ease-in-out all;
        @include bp($bp-desktop-sm) {
            margin-bottom: 24px;
            font-size: 18px;
            line-height: 24px;
            letter-spacing: 0;
        }
    }

    .product-card__price {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 16px;
            min-height: 40px;
        }
    }

    //
    // --mod
    //
    .product-card--in-cart {
        &:hover {
            border-color: var(--success);
        }
    }

    .product-card--sm {
        border-radius: 12px;
        @include bp($bp-desktop-sm) {
            border-radius: var(--border-md);
        }

        .product-card__header {
            padding: 6px;
            @include bp($bp-desktop-sm) {
                padding: 8px;
            }
        }

        .product-card__name {
            margin-bottom: 8px;
            font-size: 14px;
            line-height: 18px;
            @include bp($bp-desktop-sm) {
                margin-bottom: 24px;
            }
        }

        .product-card__img-link {
            padding-bottom: 100%;
        }

        .product-card__price {
            margin-bottom: 8px;
        }

        .product-card__wrap {
            padding: 0;
        }

        .product-card__controls {
            max-width: 100%;
            padding: 0;
        }

        .product-card__footer {
            padding: 0 8px 8px;
            @include bp($bp-desktop-sm) {
                padding: 0 12px 12px;
            }
        }
    }
</style>
